import type { SizeBand } from '@/features/types';

/**
 * THE DEGRADATION LADDER, as data — docs/DESIGN.md §5, ADR-0005, ADR-0009.
 *
 * Its own module, and free of every `@codemirror/*` import, for two mechanical reasons:
 *
 *  1. `view.ts` needs it (the tab descriptor reports what the editor will actually run) and
 *     `view.ts` is loaded EAGERLY by `features/registry.ts`. If the ladder lived in
 *     `extensions.ts`, the registry would drag the whole CodeMirror bundle into the initial
 *     chunk and `ViewDescriptor.load()` would be lazy in name only.
 *  2. It is the one part of the editor that is worth a bare-Node table test, and here it gets
 *     one (`capabilities.test.ts`, Vitest project `logic`, no DOM).
 *
 * THE INPUT IS A BAND, NOT A BYTE COUNT, and that is deliberate. The thresholds themselves
 * (`SOFT_LIMIT_BYTES`, `HARD_LIMIT_BYTES`) live in `src/app/config.ts`, which a feature may
 * not import — gate 2 denies `@/app/*` from `src/features/**`. `DocumentStore` applies those
 * thresholds once and publishes the result as `DocumentSnapshot.size`; everything downstream
 * reads the band. One threshold, one reader, no drift — which is the whole point of
 * `config.ts` existing.
 *
 * Legacy note worth keeping: the old build switched the editor off by matching a regular
 * expression against `navigator.userAgent`, so a desktop window resize could drop a user into
 * a degraded mode mid-session. Nothing here reads the user agent, the viewport, or the
 * pointer type. The only input is how many bytes the document has.
 */
export interface EditorCapabilities {
  /**
   * `false` means CodeMirror is not mounted at all and `PlainTextFallback` takes the screen.
   * Only the `over-limit` band reaches this: highlighting, folding and live diagnostics all
   * run on the main thread and would freeze the tab. The BUFFER IS NEVER TOUCHED — only the
   * editor is switched off (`editor.blocked.body`).
   */
  readonly mounted: boolean;
  /** `json()` — the Lezer grammar, and with it `syntaxHighlighting(jsonHighlight)`. */
  readonly highlight: boolean;
  /** `codeFolding()` + `foldGutter()`. First thing off above the soft limit. */
  readonly folding: boolean;
  /**
   * `bracketMatching()`. Off above the soft limit: it re-walks the syntax tree on every
   * selection change, which is the one extension whose cost scales with the document rather
   * than with the viewport.
   */
  readonly bracketMatching: boolean;
  /** `closeBrackets()` — cheap, stays on at every size the editor mounts at. */
  readonly closeBrackets: boolean;
  /** `indentOnInput()` — also cheap, also stays on. */
  readonly indentOnInput: boolean;
  /** `lintGutter()`. Diagnostics are pushed in by us; the gutter only renders them. */
  readonly lintGutter: boolean;
  /** `highlightActiveLine()` + `highlightActiveLineGutter()`. */
  readonly activeLine: boolean;
  /** `EditorView.lineWrapping`. On at every size: a horizontal scrollbar is worse. */
  readonly lineWrapping: boolean;
  /** `history()`. On at every size — undo is the safety net for Clear and for Format. */
  readonly history: boolean;
}

const FULL: EditorCapabilities = {
  mounted: true,
  highlight: true,
  folding: true,
  bracketMatching: true,
  closeBrackets: true,
  indentOnInput: true,
  lintGutter: true,
  activeLine: true,
  lineWrapping: true,
  history: true,
};

const LARGE: EditorCapabilities = {
  ...FULL,
  folding: false,
  bracketMatching: false,
};

const OFF: EditorCapabilities = {
  mounted: false,
  highlight: false,
  folding: false,
  bracketMatching: false,
  closeBrackets: false,
  indentOnInput: false,
  lintGutter: false,
  activeLine: false,
  lineWrapping: false,
  history: false,
};

const LADDER: Readonly<Record<SizeBand, EditorCapabilities>> = {
  normal: FULL,
  large: LARGE,
  'over-limit': OFF,
};

/** Pure. The same band always yields the same frozen object, so it is a valid effect dep. */
export function capabilitiesFor(band: SizeBand): EditorCapabilities {
  return LADDER[band];
}

/** The capability set of every band, for a table test or a documentation table. */
export const CAPABILITY_LADDER = LADDER;
